<div class="role-mgmt-container">
  <div class="px-4 py-3">
    <div class="role-mgmt-heading">
      <div>
        <p class="text-xl font-semibold m-0">{{ 'layout.configurations.menu.permissions' | translate }}</p>
        <p *ngIf="selectedRole" class="text-sm text-600 mt-1 mb-0">
          {{ 'layout.configurations.permissions.modifying' | translate }}:
          <strong>{{ selectedRole.name }}</strong>
        </p>
        <p *ngIf="editingRole" class="permission-grid-hint mb-0 mt-2">
          <i class="pi pi-info-circle" aria-hidden="true"></i>
          {{ 'layout.configurations.permissions.keyboardHint' | translate }}
        </p>
      </div>
    </div>

    <div class="card role-permissions-card">
      <div *ngIf="isLoading && !editingRole" class="role-permissions-skeleton" aria-hidden="true">
        <div class="role-permissions-skeleton__header">
          <p-skeleton width="8.5rem" height="2.4rem" borderRadius="0.4rem"></p-skeleton>
          <p-skeleton
            *ngFor="let i of skeletonColumns"
            width="4.75rem"
            height="2.4rem"
            borderRadius="0.4rem"></p-skeleton>
          <p-skeleton width="8.5rem" height="2.4rem" borderRadius="0.4rem"></p-skeleton>
        </div>

        <div class="role-permissions-skeleton__row" *ngFor="let row of skeletonRows">
          <p-skeleton width="8.5rem" height="2.7rem" borderRadius="0.4rem"></p-skeleton>
          <p-skeleton
            *ngFor="let i of skeletonColumns"
            width="4.75rem"
            height="2.7rem"
            borderRadius="0.4rem"></p-skeleton>
          <p-skeleton width="8.5rem" height="2.7rem" borderRadius="0.4rem"></p-skeleton>
        </div>
      </div>

      <ng-container *ngIf="!isLoading || editingRole">
        <div
          *ngIf="filteredRolePermissions.length === 0"
          class="empty-state flex flex-column align-items-center justify-content-center p-6">
          <i class="pi pi-info-circle text-4xl empty-state-icon"></i>
          <h3 class="empty-state-title">{{ 'layout.configurations.permissions.emptyState.title' | translate }}</h3>
          <p class="empty-state-description">
            {{ 'layout.configurations.permissions.emptyState.description' | translate }}
          </p>
          <p-button [label]="'layout.configurations.permissions.refreshData' | translate" icon="pi pi-refresh" (onClick)="loadRolePermissions()" severity="secondary">
          </p-button>
        </div>

        <!--
          The !isLoading guard matters: without it the matrix rendered underneath the
          spinner while the request was still in flight, showing checkbox states that
          did not belong to the roles on screen until the response replaced them.
          `editingRole` keeps the table visible during a save, which only sets
          isLoading to disable the buttons.
        -->
        <p-tabView
          *ngIf="filteredRolePermissions.length > 0 && (!isLoading || editingRole)"
          [(activeIndex)]="activeTabIndex"
          styleClass="permission-tabs"
          (onChange)="onPermissionTabChange($event.index)">
          <p-tabPanel *ngFor="let tab of permissionTabs" [cache]="false">
            <ng-template pTemplate="header">
              <i [class]="tab.icon" aria-hidden="true"></i>
              <span>{{ tab.label }}</span>
            </ng-template>

            <p-table
              [value]="filteredRolePermissions"
              [rowTrackBy]="trackByRole"
              [scrollable]="true"
              scrollHeight="calc(90vh - 15rem)"
              [tableStyle]="{ 'min-width': '820px', border: 'none' }"
              [styleClass]="'p-datatable-sm role-permissions-table' + (editingRole ? ' has-editing-row' : '')">
              <ng-template pTemplate="header">
                <tr>
                  <!--
                    Sticky columns are done in CSS, not with pFrozenColumn. PrimeNG's
                    directive measures the previous sibling's width and then writes
                    style.left on each frozen cell - a read-then-write per element. With
                    21 rows x 2 frozen columns that is ~44 forced full-table layouts per
                    render, which is what the "Forced reflow" warnings were reporting and
                    where the multi-second save/edit delay came from. position: sticky
                    gives the same result and costs nothing at runtime.
                  -->
                  <th class="role-column" rowspan="3" scope="col">
                    {{ 'layout.configurations.permissions.role' | translate }}
                  </th>

                  <th
                    *ngFor="let group of tab.groups"
                    [attr.colspan]="getGroupColumnCount(group)"
                    class="permission-group-header"
                    scope="colgroup">
                    {{ group.groupName }}
                  </th>

                  <th
                    *emPermission="Permission.CONFIG_USER_MANAGEMENT"
                    class="actions-column"
                    rowspan="3"
                    scope="col">
                    {{ 'layout.configurations.permissions.actions' | translate }}
                  </th>
                </tr>

                <tr>
                  <ng-container *ngFor="let group of tab.groups">
                    <ng-container *ngIf="group.sections">
                      <th
                        *ngFor="let section of group.sections"
                        [attr.colspan]="section.columns.length"
                        class="permission-section-header"
                        scope="colgroup">
                        {{ section.sectionName }}
                      </th>
                    </ng-container>

                    <ng-container *ngIf="!group.sections">
                      <th *ngFor="let col of group.columns" rowspan="2" class="permission-column" scope="col">
                        <div class="permission-header">
                          <span>{{ col.header }}</span>
                        </div>
                      </th>
                    </ng-container>
                  </ng-container>
                </tr>

                <tr>
                  <ng-container *ngFor="let group of tab.groups">
                    <ng-container *ngIf="group.sections">
                      <ng-container *ngFor="let section of group.sections">
                        <th *ngFor="let col of section.columns" class="permission-column" scope="col">
                          <div class="permission-header">
                            <span>{{ col.header }}</span>
                          </div>
                        </th>
                      </ng-container>
                    </ng-container>
                  </ng-container>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-role>
                <tr
                  #roleRow
                  [class.editing-row]="isRoleEditable(role.role)"
                  [ngClass]="{ 'super-admin-row': role.role === 'Super Admin' }">
                  <td class="role-cell">
                    <span class="font-medium">{{ role.role }}</span>
                    <span *ngIf="isRoleEditable(role.role)" class="modifying-text">
                      {{ 'layout.configurations.permissions.modifying' | translate }}
                    </span>
                  </td>

                  <td
                    *ngFor="let col of permissionColumns; trackBy: trackByColumn"
                    class="permission-cell"
                    [class.permission-cell--active]="isRoleEditable(role.role) && !col.disabled"
                    [class.permission-cell--selected]="!col.disabled && role[col.field]"
                    [class.permission-cell--range]="!col.disabled && isRoleEditable(role.role) && isFieldInSelectionRange(col.field)"
                    [class.permission-cell--placeholder]="col.disabled"
                    [attr.data-role-key]="!col.disabled ? role.role.trim().toLocaleLowerCase() : null"
                    [attr.data-permission-field]="!col.disabled ? col.field : null"
                    [attr.aria-label]="role.role + ': ' + col.header"
                    [attr.aria-checked]="col.disabled ? null : role[col.field]"
                    [attr.aria-disabled]="col.disabled || getPermissionDisabled(role)"
                    [attr.tabindex]="col.disabled || getPermissionDisabled(role) ? -1 : 0"
                    [attr.role]="col.disabled ? null : 'checkbox'"
                    (keydown)="!col.disabled && onPermissionKeydown($event, role, col.field)"
                    (mousedown)="!col.disabled && beginPermissionPaint($event, role, col.field, roleRow)"
                    (mouseenter)="!col.disabled && onPermissionCellMouseEnter(role, col.field)">
                    <div class="checkbox-container">
                      <!--
                        A native input, not <p-checkbox>, and no [disabled] binding.
                        The grid renders ~21 roles x ~11 columns of these; each PrimeNG
                        checkbox cost an Angular component, an ngModel FormControl and
                        ~5 DOM nodes, so a tab switch built roughly 1,200 nodes and 231
                        component instances before the first click could be handled.
                        Locking is done in CSS (see the .editing-row rules) - toggling
                        [disabled] used to enable/disable all 231 FormControls at once.
                        onPermissionChange() already ignores changes to a row that is not
                        being edited, so the guard does not depend on the markup.
                      -->
                      <input
                        *ngIf="!col.disabled; else unavailablePermission"
                        type="checkbox"
                        class="permission-checkbox"
                        [checked]="!!role[col.field]"
                        (change)="onPermissionChange(role, col.field, $any($event.target).checked)" />
                      <ng-template #unavailablePermission>
                        <i class="pi pi-minus" [pTooltip]="'layout.configurations.permissions.notYetAvailable' | translate" tooltipPosition="top" [attr.aria-label]="'layout.configurations.permissions.notYetAvailable' | translate"></i>
                      </ng-template>
                    </div>
                  </td>

                  <td
                    *emPermission="Permission.CONFIG_USER_MANAGEMENT"
                    class="actions-cell">
                    <div class="action-buttons">
                      <p-button
                        *ngIf="isRoleEditable(role.role) && role.role !== 'Super Admin'"
                        [icon]="getRowMasterIcon(role)"
                        size="small"
                        severity="secondary"
                        [text]="true"
                        (onClick)="toggleRowMasterCheckbox(role)"
                        [ariaLabel]="'layout.configurations.permissions.checkAllForRoleAria' | translate"
                        [pTooltip]="'layout.configurations.permissions.checkAllUncheckAll' | translate"
                        tooltipPosition="top">
                      </p-button>

                      <p-button
                        *ngIf="!isRoleEditable(role.role) && !editingRole && role.role !== 'Super Admin'"
                        icon="pi pi-unlock"
                        size="small"
                        severity="success"
                        [text]="true"
                        (onClick)="enableModification(role.role)"
                        [pTooltip]="'layout.configurations.tooltips.enablemodify' | translate"
                        ariaLabel="Enable permission editing">
                      </p-button>

                      <ng-container *ngIf="isRoleEditable(role.role) && role.role !== 'Super Admin'">
                        <p-button
                          [icon]="isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-check'"
                          size="small"
                          severity="success"
                          [text]="true"
                          [disabled]="isLoading"
                          (onClick)="saveChanges()"
                          [pTooltip]="'layout.configurations.tooltips.save' | translate"
                          ariaLabel="Save permission changes">
                        </p-button>
                        <p-button
                          icon="pi pi-times"
                          size="small"
                          severity="secondary"
                          [text]="true"
                          [disabled]="isLoading"
                          (onClick)="cancelEdit()"
                          [pTooltip]="'layout.configurations.tooltips.cancel' | translate"
                          ariaLabel="Cancel permission changes">
                        </p-button>
                      </ng-container>
                    </div>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </p-tabPanel>
        </p-tabView>
      </ng-container>
    </div>
  </div>
  <p-toast></p-toast>
</div>
